import IfcDoorStyleOperationEnum from "../../../enums/IfcDoorStyleOperationEnum";
import { Vector3, Plane } from 'three';
import LineloopsRepresentation2d from "../LineloopsRepresentation2d";
import { cloneAndMove, move, getArcPoints } from "./SymbolDrawingUtils";
import { buildRectangle } from "../../../utils/shapeUtils";

import { sceneUnitsFromMm } from '../../../utils/scaler';
const FRAME_WIDTH = sceneUnitsFromMm(60);

enum HandednessEnum  { LEFT = 0, RIGHT = 1 }

const buildDoorRepresentation = (width :number, thickness: number, operationType: IfcDoorStyleOperationEnum) => {

    switch (operationType) {
        case IfcDoorStyleOperationEnum.SINGLE_SWING_LEFT:
            return buildSingleSwingRepresentation(width, thickness, HandednessEnum.LEFT);
        case IfcDoorStyleOperationEnum.SINGLE_SWING_RIGHT:
            return buildSingleSwingRepresentation(width, thickness, HandednessEnum.RIGHT);

        case IfcDoorStyleOperationEnum.SLIDING_TO_LEFT:
            return buildSlidingToRepresentation(width, thickness, HandednessEnum.LEFT);
        case IfcDoorStyleOperationEnum.SLIDING_TO_RIGHT:
            return buildSlidingToRepresentation(width, thickness, HandednessEnum.RIGHT);

        case IfcDoorStyleOperationEnum.FOLDING_TO_LEFT:
            return buildFoldingToRepresentation(width, thickness, HandednessEnum.LEFT);
        case IfcDoorStyleOperationEnum.FOLDING_TO_RIGHT:
            return buildFoldingToRepresentation(width, thickness, HandednessEnum.RIGHT);

        case IfcDoorStyleOperationEnum.REVOLVING:
            return buildRevolvingRepresentation(width, thickness);

        case IfcDoorStyleOperationEnum.DOUBLE_DOOR_SINGLE_SWING:
            return buildDoubleDoorSingleSwingRepresentation(width, thickness);

        case IfcDoorStyleOperationEnum.NOTDEFINED:
            return buildNotDefinedRepresentation(width, thickness);
        
        default:
            return buildNotSupportedOperationTypeRepresentation(width, thickness);
    }
}

const buildSingleSwingRepresentation = (width :number, thickness: number, handedness: HandednessEnum ) => {

    const baseLoop = buildRectangle(0, width, 0, thickness);

    const leftFrameLine = [ new Vector3(FRAME_WIDTH, 0, 0), new Vector3(FRAME_WIDTH, thickness/2, 0) ];
    const rightFrameLine = cloneAndMove(leftFrameLine, width-FRAME_WIDTH*2, 0);
    
    let calculatedArcPoints : Vector3[] = [];

    if (handedness === HandednessEnum.LEFT) {
        calculatedArcPoints = getArcPoints(width-FRAME_WIDTH*2, 0.0, Math.PI/2, 10);
        calculatedArcPoints.push(new Vector3(0,0,0));
        move(calculatedArcPoints, FRAME_WIDTH, thickness/2);
    }
    else {
        calculatedArcPoints = getArcPoints(width-FRAME_WIDTH*2, Math.PI , Math.PI/2 , 10);
        calculatedArcPoints.push(new Vector3(0,0,0));
        move(calculatedArcPoints, width-FRAME_WIDTH, thickness/2);
    }

    return new LineloopsRepresentation2d(   [baseLoop, leftFrameLine, rightFrameLine, calculatedArcPoints], 
                                            [baseLoop], 
                                            null);
};

const buildRevolvingRepresentation = (width :number, thickness: number ) => {

    const circle_segments = 32;
    if (circle_segments % 4 !== 0) 
        throw new Error("Circle segments must be divisible by 4");

    const baseLoop = buildRectangle(0, width, 0, thickness);
    const leafWidth = width;

    // Circle
    const calculatedArcPoints = getArcPoints(leafWidth/2, 0.0, Math.PI * 2, circle_segments);
    move(calculatedArcPoints, width/2, thickness/2);

    // Circle X
    const step = Math.floor(calculatedArcPoints.length/4);
    let xIndices : number[] = [];
    for (let i=Math.ceil(step/2); i<circle_segments; i+=step) 
        xIndices.push(i);
    
    const xLine1 = [ calculatedArcPoints[xIndices[0]], calculatedArcPoints[xIndices[2]]];
    const xLine2 = [ calculatedArcPoints[xIndices[1]], calculatedArcPoints[xIndices[3]]];

    return new LineloopsRepresentation2d(   [calculatedArcPoints, xLine1, xLine2], 
                                            [baseLoop], 
                                            null);
};

const buildSlidingToRepresentation = (width :number, thickness: number,  handedness: HandednessEnum ) => {

    const yOffsetFromWall = sceneUnitsFromMm(-100);
    let opening = width * 0.15;

    if (handedness === HandednessEnum.LEFT) 
        opening = -opening;
    
    const baseLoop = buildRectangle(0, width, 0, thickness);
    const leaf = [  new Vector3(opening, yOffsetFromWall, 0), 
                    new Vector3(width+opening, yOffsetFromWall, 0) ]; 

    return new LineloopsRepresentation2d(   [baseLoop, leaf], 
                                            [baseLoop], 
                                            null);
};

const buildFoldingToRepresentation = (width :number, thickness: number,  handedness: HandednessEnum ) => {

    const foldingWidth = width * 0.25;
    const foldingHeight = foldingWidth*0.7;
    const foldingStep = foldingWidth/4;

    const baseLoop = buildRectangle(0, width, 0, thickness);
    const leaf = [  new Vector3(0, 0, 0), 
                    new Vector3(foldingStep, -foldingHeight),
                    new Vector3(foldingStep*2, 0.0),
                    new Vector3(foldingStep*3, -foldingHeight),
                    new Vector3(foldingStep*4, 0.0) ]; 

    return new LineloopsRepresentation2d(   [leaf], 
                                            [baseLoop], 
                                            null);
};

const buildDoubleDoorSingleSwingRepresentation = (width :number, thickness: number) => {

    const leafRadius = (width-FRAME_WIDTH*2)/2;

    const baseLoop = buildRectangle(0, width, 0, thickness);

    const leftFrameLine = [ new Vector3(FRAME_WIDTH, 0, 0), new Vector3(FRAME_WIDTH, thickness/2, 0) ];
    const rightFrameLine = cloneAndMove(leftFrameLine, width-FRAME_WIDTH*2, 0);
    
    const leftArcPts = getArcPoints(leafRadius, 0.0, Math.PI/2, 10);
    leftArcPts.push(new Vector3(0,0,0));
    
    const mirrorPlane = new Plane(new Vector3(1, 0, 0), width/2);
    const rightArcPts = leftArcPts.map(pt => pt.clone().reflect(mirrorPlane.normal));
    
    move(leftArcPts, FRAME_WIDTH, thickness/2);
    move(rightArcPts, FRAME_WIDTH+leafRadius*2, thickness/2);

    return new LineloopsRepresentation2d(   [baseLoop, leftFrameLine, rightFrameLine, leftArcPts, rightArcPts], 
                                            [baseLoop], 
                                            null);
};

const buildNotDefinedRepresentation = (width :number, thickness: number) => {

    const baseLoop = buildRectangle(0, width, 0, thickness);

    const firstLine = [ new Vector3(0, 0, 0), new Vector3(width, thickness, 0) ];
    const secondLine = [ new Vector3(width, 0, 0), new Vector3(0, thickness, 0) ];

    return new LineloopsRepresentation2d(   [firstLine, secondLine], 
                                            [baseLoop], 
                                            null);
};

const buildNotSupportedOperationTypeRepresentation = (width :number, thickness: number) => {

    const baseLoop = buildRectangle(0, width, 0, thickness);

    const leftFrameLine = [ new Vector3(FRAME_WIDTH, 0, 0), new Vector3(FRAME_WIDTH, thickness, 0) ];
    const rightFrameLine = cloneAndMove(leftFrameLine, width-FRAME_WIDTH*2, 0);

    return new LineloopsRepresentation2d(   [baseLoop, leftFrameLine, rightFrameLine], 
                                            [baseLoop], 
                                            null);
};

export { buildDoorRepresentation };